'use client'

import { getLog } from '@eigtech/ui-shared-logging'
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@eigtech/ui/components/alert'
import { Button } from '@eigtech/ui/components/button'
import { cn } from '@eigtech/ui/lib/utils'
import { ImageIcon, TriangleAlert, XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { formatBytes, useFileUpload } from '../hooks/use-file-upload'
import { FileEntry } from '../types/file-entry'

const log = getLog()

export interface SingleImageUploadProps {
  ref?: React.Ref<HTMLDivElement>
  accept?: string
  file?: FileEntry
  maxSize?: number
  className?: string
  disabled?: boolean
  defaultImage?: string
  onFileChange?: (file: FileEntry) => void
}

export function SingleImageUpload({
  ref,
  accept = 'image/*',
  file: fileProp,
  maxSize = 2 * 1024 * 1024, // 2MB
  className,
  disabled,
  defaultImage,
  onFileChange,
  ...props
}: SingleImageUploadProps) {
  const [
    { files, isDragging, errors },
    {
      removeFile,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      getInputProps,
    },
  ] = useFileUpload({
    files: fileProp ? [fileProp] : undefined,
    disabled,
    maxFiles: 1,
    maxSize,
    accept,
    multiple: false,
    onFilesChange: (files) => {
      onFileChange?.(files[0] || null)
    },
  })

  const currentFile = files[0]

  const handleRemove = () => {
    if (currentFile) {
      removeFile(currentFile.id)
    }
  }

  return (
    <div ref={ref} className={cn('flex flex-col items-center gap-4', className)}>
      {/* O,age Preview */}
      <div className="relative w-full">
        <div
          className={cn(
            'group/image relative h-52 w-full cursor-pointer overflow-hidden rounded-sm border border-dashed transition-colors',
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:border-muted-foreground/20',
            currentFile && 'border-solid'
          )}
          onClick={openFileDialog}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input {...getInputProps(props)} className="sr-only" />

          <FilePreview defaultImage={defaultImage} fileItem={currentFile} />
        </div>

        {/* Remove Button - only show when file is uploaded */}
        {currentFile && (
          <Button
            aria-label="Remove image"
            className="absolute end-0.5 top-0.5 z-10"
            mode="icon"
            size="xs"
            variant="destructive"
            onClick={handleRemove}
          >
            <XIcon />
          </Button>
        )}
      </div>

      {/* Upload Instructions */}
      <div className="space-y-0.5 text-center">
        <p className="text-sm font-medium">{currentFile ? 'Image selected' : 'Upload image'}</p>
        <p className="text-muted-foreground text-xs">PNG, JPG up to {formatBytes(maxSize)}</p>
      </div>

      {/* Error Messages */}
      {errors.length > 0 && (
        <Alert appearance="light" className="mt-5" variant="destructive">
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>File upload error(s)</AlertTitle>
            <AlertDescription>
              {errors.map((error, index) => (
                <p key={index} className="last:mb-0">
                  {error}
                </p>
              ))}
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}
    </div>
  )
}

function FilePreview({ fileItem, defaultImage }: { fileItem?: FileEntry; defaultImage?: string }) {
  const [thumbnail, setThumbnail] = useState<string | null>(null)

  const hasRun = useRef(false)
  const previousFileItemIdRef = useRef(fileItem?.id)

  useEffect(() => {
    const previousFileItemId = previousFileItemIdRef.current
    previousFileItemIdRef.current = fileItem?.id

    if (previousFileItemId !== previousFileItemIdRef.current) {
      hasRun.current = false
      setThumbnail(null)
    }

    if (hasRun.current || !fileItem || fileItem.status !== 'pending') return

    hasRun.current = true

    let ignore = false

    fileItem
      .getThumbnail()
      .then((thumb) => {
        if (!ignore) setThumbnail(thumb)
      })
      .catch((error) => {
        log.error('error getting thumbnail', { error })
      })

    return () => {
      ignore = true
    }
  }, [fileItem])

  return fileItem?.isImage && thumbnail ? (
    <img
      alt={fileItem.name}
      className="h-full w-full object-contain"
      src={thumbnail}
      title={`${fileItem.name} (${formatBytes(fileItem.size)})`}
    />
  ) : defaultImage ? (
    <img alt="Image" className="h-full w-full object-contain" src={defaultImage} />
  ) : (
    <div className="flex h-full w-full items-center justify-center">
      <ImageIcon className="text-muted-foreground size-6" />
    </div>
  )
}
